import axios from 'axios';

export const calculateNetworkCounts = function(notificationData) {
  if (!notificationData || !Array.isArray(notificationData) || notificationData.length === 0) {
    return {};
  }

  const networkCounts = {};

  notificationData.forEach(notification => {
    try {
      let networkSlug = null;

      // extract network from the notification
      if (notification.network) {
        networkSlug = notification.network;
      } else if (notification.notification_payload) {
        // try to get network from payload
        const payload = typeof notification.notification_payload === 'string' 
          ? JSON.parse(notification.notification_payload) 
          : notification.notification_payload;

        if (payload && payload.network) {
          networkSlug = payload.network;
        }
      }

      // if we found a network, increment its count
      if (networkSlug) {
        networkCounts[networkSlug] = (networkCounts[networkSlug] || 0) + 1;
      }
    } catch (e) {
      console.error('Error processing notification for network counts:', e, notification?.id);
    }
  });
  
  return networkCounts;
};

// module-scoped cache for singleton fetch promises
const fetchPromiseCache = {};
const fetchCountPromise = { promise: null };
const fetchNetworkCountsPromise = { promise: null };
let currentFetchPromise = null; // store the current fetch promise

/**
 * Helper function to check if a notification belongs to a different network
 * 
 * @param {Object} notification - The notification object to check
 * @param {String} networkSlug - The network slug to compare against
 * @returns {Boolean} - Returns true if the notification belongs to a different network
 */
export const isNotificationForDifferentNetwork = function(notification, networkSlug) {
  try {
    // if no networkSlug is provided or notification is empty, we can't determine if it's for a different network
    if (!networkSlug || !notification) return false;

    // first check if notification has a direct network property
    if (notification.network) {
      return notification.network !== networkSlug;
    }

    // then check the notification_payload property
    const payload = notification.notification_payload;
    if (!payload) {
      console.debug('Notification has no payload to verify network:', notification.id);
      return true; // No payload means we can't verify network
    }
    
    // handle string payloads by parsing them
    if (typeof payload === 'string') {
      try {
        const parsedPayload = JSON.parse(payload);
        return !parsedPayload.network || parsedPayload.network !== networkSlug;
      } catch (e) {
        console.error('Failed to parse notification payload:', e, notification.id);
        return true; // if we can't parse, assume it's for a different network
      }
    }
    // handle object payloads directly
    else if (payload && typeof payload === 'object') {
      return !payload.network || payload.network !== networkSlug;
    }

    // default case - assume it's for a different network
    return true;
  } catch (e) {
    console.error('Error checking notification network:', e, notification?.id, networkSlug);
    return true; // on any error, assume it's for a different network
  }
};

export const normalizeNotification = function(notification) {
  try {
    // validate input
    if (!notification || typeof notification !== 'object') {
      console.error('Invalid notification input:', notification);
      return null;
    }

    let payload;
    // parse string payloads or use object directly
    if (notification.notification_payload && typeof notification.notification_payload === 'string') {
      try {
        payload = JSON.parse(notification.notification_payload);
      } catch (parseError) {
        console.error('Failed to parse notification payload JSON:', parseError);
        return null;
      }
    } else if (notification.notification_payload) {
      payload = { ...notification.notification_payload }; // defensive copy
    } else {
      // if no payload field, use the notification itself
      payload = { ...notification }; // defensive copy
    }

    // ensure required fields are present
    if (!payload.payload_id && notification.id) {
      payload.payload_id = notification.id;
    }
    if (!payload.created_at && notification.created_at) {
      payload.created_at = notification.created_at;
    }
    if (!payload.title && notification.title) {
      payload.title = notification.title;
    }
    if (!payload.message && notification.message) {
      payload.message = notification.message;
    }

    return payload;
  } catch (e) {
    console.error('Error normalizing notification:', e, notification);
    return null;
  }
};

const getInitialState = function () {
  var state = {
    rawNotificationData: [],
    notificationCount: 0,
    networkNotificationCounts: {},
    hasLoadedNotifications: false,
    displayLimit: 20,
    isFullDataLoaded: false,
  };
  return state;
};

export default {
  namespaced: true,

  state: getInitialState(),

  getters: {
    getNotifications(state) {
      // compute normalized notifications on demand from raw data
      if (!state.rawNotificationData || !Array.isArray(state.rawNotificationData)) {
        return [];
      }
      // process and return normalized notifications
      return state.rawNotificationData
        .map(notification => normalizeNotification(notification))
        .filter(payload => payload !== null); // filter out any that failed to normalize
    },
    getPaginatedNotifications(state) {
      const notifications = state.rawNotificationData
        .map(notification => normalizeNotification(notification))
        .filter(payload => payload !== null);

      return notifications.slice(0, state.displayLimit);
    },
    getNetworkNotifications: (state, getters) => (networkSlug) => {
      // use the inverse of isNotificationForDifferentNetwork to find notifications for this network
      return getters.getNotifications.filter(notification => 
        notification.network && 
        !isNotificationForDifferentNetwork({ notification_payload: notification }, networkSlug)
      );
    },
    getPaginatedNetworkNotifications: (state, getters) => (networkSlug) => {
      const networkNotifications = getters.getNetworkNotifications(networkSlug);
      return networkNotifications.slice(0, state.displayLimit);
    },
    getNotificationsCount(state) {
      // return the dedicated count if available, otherwise calculate from raw data
      if (state.notificationCount !== null && state.notificationCount >= 0) {
        return state.notificationCount;
      }
      return state.rawNotificationData && Array.isArray(state.rawNotificationData) 
        ? state.rawNotificationData.length 
        : 0;
    },
    getNetworkNotificationCounts(state) {
      return state.networkNotificationCounts || {};
    },
    // expose the normalizeNotification helper function as a getter
    normalizeNotification: () => normalizeNotification,
  },

  mutations: {
    setNotificationCount(state, count) {
      state.notificationCount = count;
    },
    setNetworkNotificationCounts(state, networkCounts) {
      state.networkNotificationCounts = networkCounts;
    },
    setRawNotificationData(state, data) {
      state.rawNotificationData = data;
      // update count to match the raw data length
      state.notificationCount = Array.isArray(data) ? data.length : 0;
      // mark that notifications have been loaded
      state.hasLoadedNotifications = true;
    },
    addNotification(state, payload) {
      // only add if not already present in rawNotificationData
      if (!state.rawNotificationData) {
        state.rawNotificationData = [];
      }
      // check if notification already exists by payload_id
      const payloadId = payload.payload_id || payload.id;
      if (!payloadId) {
        console.error('Notification has no ID, cannot add to store:', payload);
        return;
      }
      const exists = state.rawNotificationData.some(n => 
        (n.id === payloadId) || (n.payload_id === payloadId)
      );
      
      if (!exists) {
        // create a raw notification structure
        const rawNotification = {
          id: payloadId,
          created_at: payload.created_at,
          title: payload.title,
          message: payload.message,
          network: payload.network,
          notification_payload: payload
        };
        // add to raw notification data
        state.rawNotificationData.push(rawNotification);
        // update the count
        state.notificationCount = state.rawNotificationData.length;
      } else {
        // notification already exists, no need to add it again
      }
    },
    removeNotification(state, index) {
      if (index >= 0 && index < state.rawNotificationData.length) {
        state.rawNotificationData.splice(index, 1);
        state.notificationCount = state.rawNotificationData.length;
      }
    },
    clearAllNotifications(state) {
      // clear all notifications
      state.rawNotificationData = [];
      state.notificationCount = 0;
    },
    RESET_STATE(state) {
      // reset to initial state
      const initialState = getInitialState();
      Object.keys(initialState).forEach(key => {
        state[key] = initialState[key];
      });
    },
    increaseDisplayLimit(state, increment) {
      state.displayLimit += increment;
    },
    setIsFullDataLoaded(state, value) {
      state.isFullDataLoaded = value;
    },
    setHasLoadedNotifications(state, value) {
      state.hasLoadedNotifications = value;
    },
  },

  actions: {
    // fetch notification data from the API and update both raw data and count
    // this is the primary method that should be called to load notifications
    fetchNotifications(context, { force = false, networkSlug = null, limit = 'all', isRetry = false } = {}) {
      // use a module-level singleton promise to prevent duplicate API calls
      // if force=true, we'll bypass the singleton and make a fresh API call
      const cacheKey = `_fetchPromise_${networkSlug || 'all'}`;

      if (force || !fetchPromiseCache[cacheKey]) {
        fetchPromiseCache[cacheKey] = true; // set flag immediately to prevent race conditions

        // if we have existing data and we're not forcing a refresh, just use it
        if (!force && context.state.rawNotificationData && context.state.rawNotificationData.length > 0 && context.state.isFullDataLoaded) {
          // mark that notifications have been loaded even if we're using cached data
          context.commit('setHasLoadedNotifications', true);

          currentFetchPromise = Promise.resolve({
            notifications: networkSlug ? 
              context.getters.getNetworkNotifications(networkSlug) : 
              context.getters.getNotifications,
            count: networkSlug ? 
              context.state.networkNotificationCounts[networkSlug] || 0 : 
              context.state.notificationCount
          });
        } else {
          // otherwise fetch from API
          let url = '/auth/notification-payloads';
          let params = { limit };

          // if networkSlug is provided and valid, add it to the query params
          if (networkSlug && typeof networkSlug === 'string' && networkSlug.trim() !== '' && networkSlug !== 'all') {
            // ensure we're passing a clean network slug
            params.network = networkSlug.trim();
          }

          currentFetchPromise = axios.get(url, { params })
            .then(response => {
              const notificationsData = response.data.data || response.data || [];

              // store the raw notification data (this will also update the count)
              // only update if we actually got data to avoid clearing existing data on API error
              if (notificationsData && notificationsData.length > 0) {
                if (networkSlug) {
                  // if loading for a specific network, merge with existing notifications
                  // first remove existing notifications for this network using the helper function
                  const otherNetworkNotifications = context.state.rawNotificationData.filter(notification => 
                    isNotificationForDifferentNetwork(notification, networkSlug)
                  );
                  // then merge with the new network-specific notifications
                  context.commit('setRawNotificationData', [...otherNetworkNotifications, ...notificationsData]);
                } else {
                  // if loading all notifications, replace existing ones
                  context.commit('setRawNotificationData', notificationsData);
                  context.commit('setIsFullDataLoaded', true);

                  // calculate and update network counts from the full notification data
                  const calculatedNetworkCounts = calculateNetworkCounts(notificationsData);
                  if (Object.keys(calculatedNetworkCounts).length > 0) {
                    console.debug('[NOTIFICATIONS] Updating network counts from full notification data');
                    context.commit('setNetworkNotificationCounts', calculatedNetworkCounts);
                  }
                }
              }

              // mark that notifications have been loaded
              context.commit('setHasLoadedNotifications', true);

              return {
                notifications: networkSlug ? 
                  context.getters.getNetworkNotifications(networkSlug) : 
                  context.getters.getNotifications,
                count: networkSlug ? 
                  (context.state.networkNotificationCounts[networkSlug] || notificationsData.length) : 
                  context.state.notificationCount
              };
            })
            .catch(error => {
              console.error('Error fetching notifications:', error);
              return {
                notifications: [],
                count: 0
              };
            });
        }

        // set a cleanup timeout that is long enough to cover page initialization
        // this ensures we don't make duplicate API calls during initial page load
        setTimeout(() => {
          fetchPromiseCache[cacheKey] = false;
        }, 2000); // 2 seconds should be enough for all components to initialize
      } else {
        if (!currentFetchPromise) {
          // instead of using polling, immediately resolve with current data or trigger a fetch
          // components should explicitly set force=true when they need fresh data
          if (context.state.hasLoadedNotifications) {
            // if we have already loaded notifications before, return what we have
            currentFetchPromise = Promise.resolve({
              notifications: networkSlug ? 
                context.getters.getNetworkNotifications(networkSlug) : 
                context.getters.getNotifications,
              count: networkSlug ? 
                context.state.networkNotificationCounts[networkSlug] || 0 : 
                context.state.notificationCount
            });
          } else {
            // check for recursion - if this is already a retry, don't recurse again
            if (isRetry) {
              console.warn('Preventing potential infinite recursion in fetchNotifications - already retried once');
              // return empty data rather than recursing again
              currentFetchPromise = Promise.resolve({
                notifications: [],
                count: 0
              });
            } else {
              // if we haven't loaded any data yet, trigger a fresh fetch with force=true
              // this ensures we don't get caught in a situation where no data is ever loaded
              fetchPromiseCache[cacheKey] = false; // reset the cache key to allow a new fetch
              return context.dispatch('fetchNotifications', { 
                force: true, 
                networkSlug, 
                limit,
                isRetry: true // set the retry flag to prevent further recursion
              });
            }
          }
        }
      }
      return currentFetchPromise;
    },
    fetchNotificationCount(context, { force = false } = {}) {
      // if we already have full notification data loaded, use that count instead of calling the API
      if (!force && context.state.hasLoadedNotifications && context.state.isFullDataLoaded) {
        return Promise.resolve(context.state.notificationCount);
      }

      // use a separate singleton to prevent duplicate count API calls
      if (force || !fetchCountPromise.promise) {
        fetchCountPromise.promise = axios.get('/auth/notification-payloads/count')
          .then(response => {
            const count = response.data.count || 0;
            context.commit('setNotificationCount', count);
            return count;
          })
          .catch(error => {
            console.error('Error fetching notification count:', error);
            return 0;
          })
          .finally(() => {
            // clear the singleton after a delay
            setTimeout(() => {
              fetchCountPromise.promise = null;
            }, 1000);
          });
      } else {
        console.debug('[NOTIFICATIONS] Using in-progress notification count request');
      }
      return fetchCountPromise.promise;
    },
    fetchNetworkNotificationCounts(context, { force = false } = {}) {
      // if we already have full notification data loaded and network counts populated, use that instead
      if (!force && 
          context.state.hasLoadedNotifications && 
          context.state.isFullDataLoaded) {
        
        // if we already have network counts calculated and stored, use those
        if (Object.keys(context.state.networkNotificationCounts).length > 0) {
          console.debug('[NOTIFICATIONS] Using existing cached network counts');
          return Promise.resolve(context.state.networkNotificationCounts);
        }

        // otherwise, calculate them from the raw notification data
        if (context.state.rawNotificationData && context.state.rawNotificationData.length > 0) {
          console.debug('[NOTIFICATIONS] Calculating network counts from cached notification data');
          const calculatedCounts = calculateNetworkCounts(context.state.rawNotificationData);

          // store the calculated counts
          context.commit('setNetworkNotificationCounts', calculatedCounts);

          return Promise.resolve(calculatedCounts);
        }
      }
      
      // use a separate singleton to prevent duplicate API calls
      if (force || !fetchNetworkCountsPromise.promise) {
        fetchNetworkCountsPromise.promise = axios.get('/auth/notification-payloads/network-counts')
          .then(response => {
            const networkCounts = response.data.networks || {};
            const totalCount = response.data.total || 0;

            // store the network counts
            context.commit('setNetworkNotificationCounts', networkCounts);

            // also update the total count if available
            if (totalCount > 0) {
              context.commit('setNotificationCount', totalCount);
            }

            return networkCounts;
          })
          .catch(error => {
            console.error('Error fetching network notification counts:', error);
            return {};
          })
          .finally(() => {
            // clear the singleton after a delay
            setTimeout(() => {
              fetchNetworkCountsPromise.promise = null;
            }, 1000);
          });
      } else {
        console.debug('[NOTIFICATIONS] Using in-progress network counts request');
      }
      return fetchNetworkCountsPromise.promise;
    },
    fetchNetworkNotifications(context, { networkSlug, force = false, limit = 'all', isRetry = false }) {
      if (!networkSlug) {
        return Promise.resolve([]);
      }

      return context.dispatch('fetchNotifications', { 
        networkSlug, 
        force, 
        limit,
        isRetry
      });
    },
    // legacy methods that now use fetchNotifications internally
    // these methods maintain backward compatibility with existing code
    getNotificationCount(context, { force = false } = {}) {
      // first check if we have a count already in the state
      if (!force && context.state.notificationCount > 0) {
        console.debug('[NOTIFICATIONS] Using cached notification count from state:', context.state.notificationCount);
        return Promise.resolve(context.state.notificationCount);
      }

      // then check if we have full notifications loaded
      if (!force && context.state.hasLoadedNotifications && context.state.rawNotificationData.length > 0) {
        console.debug('[NOTIFICATIONS] Calculating count from cached notifications:', context.state.rawNotificationData.length);
        return Promise.resolve(context.state.rawNotificationData.length);
      }

      // otherwise, use the lighter-weight count-only method for count requests
      return context.dispatch('fetchNotificationCount', { force });
    },
    getNotifications(context, { force = false, isRetry = false } = {}) {
      // if we already have notifications loaded and not forcing a refresh, use the cached data
      if (!force && context.state.hasLoadedNotifications && context.state.rawNotificationData.length > 0) {
        return Promise.resolve(context.getters.getNotifications);
      }
      // otherwise fetch full notifications
      return context.dispatch('fetchNotifications', { force, isRetry })
        .then(result => result.notifications);
    },
    addNotification(context, payload) {
      context.commit('addNotification', payload);
    },
    removeNotification(context, index) {
      // get the normalized notifications to find the payload ID
      const normalizedNotifications = context.getters.getNotifications;

      if (index >= 0 && index < normalizedNotifications.length) {
        const payloadId = normalizedNotifications[index].payload_id;

        // find the corresponding index in raw data
        const rawIndex = context.state.rawNotificationData.findIndex(n =>
          (n.id === payloadId) || (n.payload_id === payloadId));

        if (rawIndex !== -1) {
          // remove from store using the correct raw data index
          context.commit('removeNotification', rawIndex);
        }

        // make the API call to delete the notification on the server
        return axios.delete('/auth/notification-payloads/' + payloadId)
          .catch(error => {
            console.error('Error removing notification:', error);
          });
      }

      return Promise.resolve();
    },
    // remove a notification by its payload ID instead of index
    removeNotificationByPayloadId(context, payloadId) {
      if (!payloadId) {
        return Promise.resolve();
      }

      // find the corresponding index in raw data
      const rawIndex = context.state.rawNotificationData.findIndex(n =>
        (n.id === payloadId) || (n.payload_id === payloadId));

      if (rawIndex !== -1) {
        // remove from store using the correct raw data index
        context.commit('removeNotification', rawIndex);
      }

      // make the API call to delete the notification on the server
      return axios.delete('/auth/notification-payloads/' + payloadId)
        .catch(error => {
          console.error('Error removing notification:', error);
        });
    },
    clearAllNotifications(context) {
      // make the API call to clear all notifications
      return axios.delete('/auth/notification-payloads')
        .then(() => {
          context.commit('clearAllNotifications');
        })
        .catch(error => {
          console.error('Error clearing notifications:', error);
          // still clear local state even if API call fails
          context.commit('clearAllNotifications');
        });
    },
    resetNotifications(context) {
      context.commit('RESET_STATE');
    },
    // increase the display limit to show more notifications
    increaseDisplayLimit(context, { increment = 20 } = {}) {
      context.commit('increaseDisplayLimit', increment);
      return Promise.resolve(context.getters.getPaginatedNotifications);
    },
  },
};
